import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../../lib/api";
import { trackCta } from "../../lib/analytics";

const LINKS = [
  { label: "Home", id: "top" },
  { label: "Our Process", id: "process" },
  { label: "Results", id: "results" },
  { label: "Pricing", id: "pricing" },
];

export function useSectionNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (id) => {
    if (pathname === "/") {
      if (id === "top") { window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }); return; }
      scrollToId(id);
    } else navigate(id === "top" ? "/" : `/#${id}`);
  };
}

export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const go = useSectionNav();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const click = (id) => { setOpen(false); go(id); };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled || open ? "bg-ink/75 backdrop-blur-xl border-white/[0.07]" : "border-transparent"}`}>
      <nav className="container-x flex h-[68px] items-center justify-between" aria-label="Main">
        <Logo />
        <ul className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <li key={l.id}>
              <button data-testid={`nav-${l.id}-link`} onClick={() => click(l.id)}
                className="rounded-full px-4 py-2 text-sm text-slate-300 transition-colors duration-200 hover:text-white">
                {l.label}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2">
          <Link to="/get-started" data-testid="nav-get-started-btn" onClick={() => trackCta("Get Started", "nav")}
            className="btn-primary !px-5 !py-2.5 hidden sm:inline-flex">
            Get Started <ArrowRight className="h-4 w-4" />
          </Link>
          <button data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
            onClick={() => setOpen((o) => !o)} className="grid h-11 w-11 place-items-center rounded-full border border-white/10 md:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/[0.07] md:hidden" data-testid="nav-mobile-menu">
            <ul className="container-x flex flex-col py-4">
              {LINKS.map((l) => (
                <li key={l.id}>
                  <button data-testid={`nav-mobile-${l.id}-link`} onClick={() => click(l.id)}
                    className="w-full py-3.5 text-left text-2xl font-medium tracking-tight text-white">{l.label}</button>
                </li>
              ))}
              <li className="pt-3">
                <Link to="/get-started" onClick={() => setOpen(false)} data-testid="nav-mobile-get-started-btn" className="btn-primary w-full">
                  Get Started <ArrowRight className="h-4 w-4" />
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
